import { AfterViewChecked, Component, OnInit } from '@angular/core';
import { ParamMap } from '@angular/router';
import { forkJoin, from, of } from 'rxjs';
import { debounceTime, mergeMap } from 'rxjs/operators';
import { OrderInfo, OrderItemInfo, PaymentInfo } from 'src/app/models/models';
import { Billing_Data, Currency_Rate, Payment, Store, Table } from 'src/app/models/RestModels';
import { SearchObject } from 'src/app/services/Rest';
import { environment } from 'src/environments/environment';
import { BaseComponent } from '../base/base.component';
import Hashids from 'hashids';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { GetEmpty } from 'src/app/classes/Empties';

declare function isMobile(): boolean;

interface FacturaItem {
	item_name: string;
	qty: number;
	valor_unitario: number;  // precio sin impuesto (2 decimales)
	importe: number;         // valor_unitario × qty
	descuento: number;       // ajuste por redondeo
	base: number;            // subtotal original para calcular impuesto
	iva: number;             // impuesto calculado sobre base
	ieps: number;            // IEPS si aplica
	tax_percent: number;
	ieps_percent: number;
	note?: string;
	exceptions?: string;
	batch_option?: 'NONE' | 'BATCH_ONLY' | 'EXPIRATION_ONLY' | 'BATCH_AND_EXPIRATION';
	batches: { batch?: string; expiration_date?: string }[];
}

@Component({
	selector: 'app-print-receipt8',
	templateUrl: './print-receipt8.component.html',
	styleUrls: ['./print-receipt8.component.css']
})
export class PrintReceipt8Component extends BaseComponent implements OnInit, AfterViewChecked
{
	payment_info: PaymentInfo = null;
	order_info: OrderInfo | null;
	print_and_close: boolean = false;
	print_copies: number = 1;
	copies: number[] = [1];
	that: any = this;
	discount: number = 0;
	force_close: boolean = false;
	has_dolars: boolean = false;
	currency_rate: Currency_Rate | null = null;
	billing_data: Billing_Data | null = null;
	active: boolean = false;
	is_mobile: boolean = false;

	// Valores calculados estilo factura
	factura_items: FacturaItem[] = [];
	factura_subtotal: number = 0;      // suma de importes
	factura_descuento: number = 0;     // suma de descuentos (ajuste redondeo)
	factura_subtotal_neto: number = 0; // subtotal - descuento
	factura_iva: number = 0;
	factura_ieps: number = 0;
	factura_total: number = 0;

	store: Store = GetEmpty.store();

	url_for_facturacion: string = 'https://' + window.location.hostname + '/facturar';
	element_type = NgxQrcodeElementTypes.URL;
	error_correction_level: NgxQrcodeErrorCorrectionLevels.HIGH;

	hashids = new Hashids(
		environment.app_settings.hashids_salt,
		environment.app_settings.hashids_length,
		environment.app_settings.hashids_alphabet
	);

	legend: Record<string, string> = {
		'CASH-MXN': 'Efectivo',
		'CASH-USD': 'Dolares',
		'CREDIT_CARD-MXN': 'T. Credito',
		'DEBIT_CARD-MXN': 'T. Debito',
		'CHECK-MXN': 'Cheque',
		'TRANSFER-MXN': 'Transferencia',
		'RETURN_DISCOUNT-MXN': 'Descuento',
		'DIGITAL_WALLET-MXN': 'Monedero',
	}

	service_type_dictionary: Record<string, string> = {
		'TOGO': 'A DOMICILIO',
		'IN_PLACE': '',
		'PICK_UP': 'PARA LLEVAR',
		'QUICK_SALE': '',
	}

	beforeprint = () => {
		console.log('beforeprint');
		if (this.rest.printer_ticket_config) {
			document.getElementById('print-config').innerHTML = this.rest.printer_ticket_config;
			return;
		}
		document.getElementById('print-config').innerHTML = '';
	};

	afterprint = function () {
		console.log('afterprint');
		document.getElementById('print-config').innerHTML = '';
	};

	show_bill_code: boolean = true;
	table: Table | null = null;
	return_to_pos: boolean = false;

	ngOnDestroy(): void {
		this.active = false;
		window.removeEventListener('beforeprint', this.beforeprint);
		window.removeEventListener('afterprint', this.afterprint);
		super.ngOnDestroy();
	}

	ngAfterViewInit() {
	}

	ngAfterViewChecked() {
		this.active = true;
	}

	ngOnInit(): void {
		window.addEventListener('beforeprint', this.beforeprint);
		window.addEventListener('afterprint', this.afterprint);

		this.is_mobile = isMobile();

		this.subs.sink = this.route.paramMap.pipe(
			debounceTime(200)
		)
		.subscribe((paramMap: ParamMap) => {
			this.currency_rate = null;
			this.print_and_close = paramMap.has('print');
			this.print_copies = paramMap.has('copies') ? parseInt(paramMap.get('copies')) : 1;
			this.force_close = paramMap.has('print');
			this.return_to_pos = paramMap.has('return_pos') && paramMap.get('return_pos') == '1';

			console.log('Print copies has', this.copies, 'Cerrar', this.force_close);
			let c = new Array(this.print_copies);
			c.fill(1);
			this.copies = c;

			let search: SearchObject<Payment> = this.getEmptySearch();
			search.search_extra = { order_id: paramMap.get('order_id') };
			search.limit = 1;
			search.sort_order = ['id_ASC'];

			if (this.print_and_close) {
				console.log('Adding force close', this.afterprint);
				window.onafterprint = this.afterprint
			}

			console.log("Se inicializo cuantas veces");

			let something = paramMap.has('order_id') ? paramMap.get('order_id') : paramMap.get('order_sync_id');

			this.setTitle('FacturaTicket-' + something);

			if (paramMap.has('order_id')) {
				this.subs.sink = this.rest.order_info.get(paramMap.get('order_id')).pipe(
					mergeMap((response) => {
						return forkJoin({
							order_info: of(response),
							table: response.order.table_id ? this.rest.getTable(response.order.table_id) : of(null),
						})
					}),
					mergeMap((resp) => {
						let order_info = resp.order_info;
						return forkJoin({
							order: of(resp.order_info),
							store: this.rest.getStore(order_info.order.store_id),
							payment_info: this.rest.payment_info.search(search),
							billing_data: this.rest.getBillingData(true),
							price_type: this.rest.getPriceTypes(),
							table: of(resp.table)
						})
					})
				)
				.subscribe((responses) => {
					this.rest.printer_ticket_config = responses.store.printer_ticket_config;
					this.rest.createStructuredItems(responses.order);
					this.billing_data = responses.billing_data.data.find(bd => bd.id == responses.store.default_billing_data_id) || null;
					this.table = responses.table;

					responses.order.price_type = responses.price_type.data.find((pt) => pt.id == responses.order.order.price_type_id);
					this.order_info = responses.order;
					this.setShowCode();

					// Calcular valores estilo factura
					this.calculateFacturaValues(responses.order);

					if (responses.payment_info.data.length) {
						this.payment_info = responses.payment_info.data[0];
					}

					if (this.payment_info?.movements) {
						dance:
						for (let bmi of this.payment_info.movements) {
							for (let bmo of bmi.bank_movement_orders) {
								if (bmo.currency_id != this.order_info.store.default_currency_id) {
									this.currency_rate = {
										store_id: responses.order.store.id,
										currency_id: bmo.currency_id,
										rate: bmo.exchange_rate
									};
									break dance;
								}
							}
						}
					}

					if (this.print_and_close) {
						console.log('Pringint');
						let delay = (this.is_mobile ? 2000 : 700) + (responses.store.print_delay || 0);
						setTimeout(() => {
							if (this.active) {
								window.print()
							}

							if (this.return_to_pos) {
								this.router.navigate(['/pos']);
							}
							else if (!this.is_mobile) {
								window.close();
							}
						}, delay);
					}
				}, (error) => this.showError(error));
			}
			else {
				this.subs.sink = forkJoin({
					order: from(this.rest.getOfflineOrderInfoBySyncId(paramMap.get('order_sync_id'))),
					payment: from(this.rest.getOfflinePaymentByOderSyncId(paramMap.get('order_sync_id'))),
				})
				.subscribe((response) => {
					try {
						this.payment_info = response.payment;
						this.order_info = response.order;

						// Calcular valores estilo factura
						this.calculateFacturaValues(response.order);

						if (this.payment_info.movements) {
							dance:
							for (let bmi of response.payment.movements) {
								for (let bmo of bmi.bank_movement_orders) {
									if (bmo.currency_id != this.order_info.store.default_currency_id) {
										this.currency_rate = {
											store_id: response.order.store.id,
											currency_id: bmo.currency_id,
											rate: bmo.exchange_rate
										};
										break dance;
									}
								}
							}
						}

						console.log('Buscqando order ', response);
						console.log('payment info ', this.payment_info);

						if (this.print_and_close) {
							console.log('Pringint');
							setTimeout(() => {
								window.print()

								if (!this.is_mobile)
									window.close();
							}, (this.is_mobile ? 2000 : 700));
						}
					}
					catch (e) {
						console.error('error ocurrido', e);
						this.showError('Ocurrio un error aqui');
					}
				});
			}
		});
	}

	/**
	 * Calcula los valores como aparecerían en la factura CFDI 4.0
	 * Siguiendo la documentación en Notes/factura_ticket_calculation.md
	 *
	 * Usa los valores originales del order_item (subtotal, tax) que ya están
	 * almacenados en la base de datos, y calcula valor_unitario redondeando
	 * hacia ARRIBA para que el importe >= base (descuento siempre positivo).
	 */
	calculateFacturaValues(order_info: OrderInfo): void {
		this.factura_items = [];
		let total_importe = 0;
		let total_descuento = 0;
		let total_iva = 0;
		let total_ieps = 0;

		for (let oii of order_info.items) {
			if (oii.order_item.type == 'REFUND') continue;

			const tax_percent = this.getItemTax(order_info, oii);
			const ieps_percent = oii.order_item.ieps_type == 'RATE' ? (oii.order_item.ieps_value || 0) : 0;
			const qty = oii.order_item.qty;

			// Usar el subtotal original del order_item como base
			// Este valor ya está calculado y almacenado en la BD
			const base = oii.order_item.subtotal;

			// Calcular valor unitario redondeando hacia ARRIBA (Math.ceil)
			// Esto garantiza que importe >= base, por lo tanto descuento >= 0
			const valor_unitario_preciso = base / qty;
			const valor_unitario = Math.ceil(valor_unitario_preciso * 100) / 100;

			// Calcular importe (valor unitario redondeado × cantidad)
			const importe = Math.round(valor_unitario * qty * 100) / 100;

			// Calcular descuento de ajuste (diferencia por redondeo)
			// Siempre será >= 0 porque redondeamos hacia arriba
			const descuento = Math.round((importe - base) * 100) / 100;

			// Usar el impuesto original del order_item
			const iva = oii.order_item.tax || 0;
			const ieps_value = ieps_percent > 0 ? Math.round(base * (ieps_percent / 100) * 100) / 100 : 0;

			this.factura_items.push({
				item_name: oii.item.name,
				qty: qty,
				valor_unitario: valor_unitario,
				importe: importe,
				descuento: descuento,
				base: base,
				iva: iva,
				ieps: ieps_value,
				tax_percent: tax_percent,
				ieps_percent: ieps_percent,
				note: oii.order_item.note || undefined,
				exceptions: oii.order_item.exceptions || undefined,
				batch_option: oii.item.batch_option || 'NONE',
				batches: (oii.batches || []).map(b => ({ batch: b.batch || undefined, expiration_date: b.expiration_date || undefined }))
			});

			total_importe += importe;
			total_descuento += descuento;
			total_iva += iva;
			total_ieps += ieps_value;
		}

		this.factura_subtotal = Math.round(total_importe * 100) / 100;
		this.factura_descuento = Math.round(total_descuento * 100) / 100;
		this.factura_subtotal_neto = Math.round((total_importe - total_descuento) * 100) / 100;
		this.factura_iva = Math.round(total_iva * 100) / 100;
		this.factura_ieps = Math.round(total_ieps * 100) / 100;
		// El total debe coincidir con el total original de la orden
		this.factura_total = Math.round((order_info.order.total - (order_info.order.discount || 0)) * 100) / 100;
	}

	facturar(_evt: Event) {
		let url = this.rest.getUrlBase() + '/facturar.php?id=' + this.order_info.order.id;

		this.subs.sink = this.rest
			.getUrlAsString(url)
			.subscribe((response) => {
				this.showSuccess('Exito');
			}, (error) => {
				this.showError(error);
			})
	}

	justPrint() {
		window.print();
	}

	closeWindow() {
		window.close();
	}

	setShowCode() {
		this.show_bill_code = this.order_info &&
			this.order_info.order.status == 'CLOSED' &&
			this.order_info.order.cancellation_timestamp == null &&
			this.order_info.store.autofacturacion_settings != 'DISABLED' &&
			this.order_info.store.default_billing_data_id &&
			this.order_info?.price_type?.show_bill_code != 'NO'
	}

	getItemTax(order_info: OrderInfo, ii: OrderItemInfo): number {
		return ii.item.applicable_tax == 'DEFAULT'
			? order_info.order.tax_percent
			: ii.item.tax_percent;
	}
}
